import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import CreatePost from './CreatePost'
import PostCard from './PostCard'
import FriendsSidebar from './FriendsSidebar'
import MessagesPanel from './MessagesPanel'
import UserProfile from './UserProfile'
import { useQuery } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { getUserId, resolveProfileUserId } from '../utils/sessionUser'
import './SocialFeed.css'

function SocialFeed() {
  const { user } = useAuth()
  // Récupérer selectedUserId depuis localStorage (persiste après rechargement / nouvel onglet)
  const [selectedUserId, setSelectedUserId] = useState(() => {
    try {
      return localStorage.getItem('social_selectedUserId') || null
    } catch {
      return null
    }
  })
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = selectedUserId && localStorage.getItem('social_lastActiveTab')
      return (selectedUserId && (stored || 'profile')) || 'feed'
    } catch {
      return 'feed'
    }
  })
  const [selectedConversation, setSelectedConversation] = useState(null)
  const glassSidebarRef = useRef(null)
  const socialNavRef = useRef(null)

  // Récupérer le profil complet de l'utilisateur pour avoir display_name et avatar_url
  const currentUserId = getUserId(user)
  const { data: userProfile } = useQuery({
    queryKey: ['social-user-profile', currentUserId],
    queryFn: () => socialService.getUserProfile(currentUserId),
    enabled: !!currentUserId,
    retry: false
  })

  const currentUserProfile = userProfile?.user || user

  useLayoutEffect(() => {
    const nav = socialNavRef.current
    if (!nav) return undefined

    const sync = () => {
      const active = nav.querySelector('.nav-item.active')
      if (!active) return
      const parent = nav.getBoundingClientRect()
      const box = active.getBoundingClientRect()
      nav.style.setProperty('--social-ind-x', `${box.left - parent.left}px`)
      nav.style.setProperty('--social-ind-y', `${box.top - parent.top}px`)
      nav.style.setProperty('--social-ind-w', `${box.width}px`)
      nav.style.setProperty('--social-ind-h', `${box.height}px`)
    }

    sync()
    const frame = window.requestAnimationFrame(sync)
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(sync) : null
    observer?.observe(nav)
    window.addEventListener('resize', sync)
    window.addEventListener('orientationchange', sync)
    return () => {
      window.cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', sync)
      window.removeEventListener('orientationchange', sync)
    }
  }, [activeTab])

  // Sauvegarder selectedUserId et activeTab dans localStorage (persiste après rechargement)
  useEffect(() => {
    try {
      if (selectedUserId) {
        localStorage.setItem('social_selectedUserId', selectedUserId)
        localStorage.setItem('social_lastActiveTab', activeTab)
      } else {
        localStorage.removeItem('social_selectedUserId')
      }
    } catch (_) {}
  }, [selectedUserId, activeTab])

  // Écouter l'événement de token expiré pour préserver l'état
  useEffect(() => {
    const handleTokenExpired = () => {
      try {
        if (selectedUserId) {
          localStorage.setItem('social_selectedUserId', selectedUserId)
          localStorage.setItem('social_lastActiveTab', activeTab)
        }
      } catch (_) {}
    }
    window.addEventListener('auth:token-expired', handleTokenExpired)
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired)
  }, [selectedUserId, activeTab])

  // Écouter l'événement pour sélectionner une conversation depuis les notifications
  useEffect(() => {
    const handleSelectConversation = (event) => {
      const { userId } = event.detail || {}
      console.log('[SocialFeed] Événement selectConversation reçu:', userId)
      if (userId) {
        console.log('[SocialFeed] Changement vers onglet messages et sélection de la conversation:', userId)
        setActiveTab('messages')
        setSelectedConversation(userId)
        // Nettoyer sessionStorage après utilisation
        sessionStorage.removeItem('pendingConversationId')
      }
    }
    
    // Vérifier s'il y a une conversation en attente dans sessionStorage
    const pendingConversationId = sessionStorage.getItem('pendingConversationId')
    if (pendingConversationId) {
      console.log('[SocialFeed] Conversation en attente trouvée dans sessionStorage:', pendingConversationId)
      setActiveTab('messages')
      setSelectedConversation(pendingConversationId)
      sessionStorage.removeItem('pendingConversationId')
    }
    
    // Écouter l'événement immédiatement
    window.addEventListener('selectConversation', handleSelectConversation)
    
    return () => {
      window.removeEventListener('selectConversation', handleSelectConversation)
    }
  }, [])

  // Récupérer le feed
  const { data: feedData, isLoading, error, refetch } = useQuery({
    queryKey: ['social-feed'],
    queryFn: () => socialService.getFeed(),
    refetchInterval: (query) => {
      // Ne pas rafraîchir si l'endpoint n'existe pas (405)
      if (query.state.error?.is405 || query.state.error?.response?.status === 405) {
        return false
      }
      return 30000 // Rafraîchir toutes les 30 secondes
    },
    retry: false, // Ne pas réessayer en cas d'erreur
    enabled: true,
    // Ne pas logger les erreurs 405 dans la console
    onError: (error) => {
      if (error.is405 || error.response?.status === 405) {
        // Erreur 405 silencieuse - endpoint non disponible
        return
      }
      console.error('Erreur lors du chargement du feed:', error)
    }
  })

  const posts = feedData?.posts || []

  // Effet glass distortion au survol
  useEffect(() => {
    const glassSidebar = glassSidebarRef.current
    if (!glassSidebar) return

    const handleMouseMove = (e) => {
      const rect = glassSidebar.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      // Mettre à jour le filtre de distortion
      const filter = document.querySelector('#glass-distortion feDisplacementMap')
      if (filter) {
        const scaleX = (x / rect.width) * 100
        const scaleY = (y / rect.height) * 100
        filter.setAttribute('scale', Math.min(scaleX, scaleY))
      }
      
      // Ajouter l'effet de highlight
      const specular = glassSidebar.querySelector('.glass-specular')
      if (specular) {
        specular.style.background = `radial-gradient(
          circle at ${x}px ${y}px,
          rgba(255,255,255,0.15) 0%,
          rgba(255,255,255,0.05) 30%,
          rgba(255,255,255,0) 60%
        )`
      }
    }

    const handleMouseLeave = () => {
      const filter = document.querySelector('#glass-distortion feDisplacementMap')
      if (filter) {
        filter.setAttribute('scale', '77')
      }
      
      const specular = glassSidebar.querySelector('.glass-specular')
      if (specular) {
        specular.style.background = 'none'
      }
    }

    glassSidebar.addEventListener('mousemove', handleMouseMove)
    glassSidebar.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      glassSidebar.removeEventListener('mousemove', handleMouseMove)
      glassSidebar.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  return (
    <div className="social-feed-container">
      {/* SVG Filter for Glass Distortion */}
      <svg style={{ display: 'none' }}>
        <filter id="glass-distortion">
          <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
        </filter>
      </svg>

      <div className="social-layout">
        {/* Menu latéral gauche en liquid glass */}
        <div className="glass-sidebar" ref={glassSidebarRef}>
          <div className="glass-filter"></div>
          <div className="glass-overlay"></div>
          <div className="glass-specular"></div>
          <div className="glass-content">
            <div className="sidebar-header">
              <img src="/Logo Cam'it Blanc Horizontal.png" alt="Cam'it" className="logo-camit" />
            </div>
            <div className="social-nav-bar">
              <nav className="sidebar-nav" aria-label="Réseau social" ref={socialNavRef}>
                <span className="social-nav-indicator" aria-hidden="true" />
                <button 
                  type="button"
                  className={`nav-item ${activeTab === 'feed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('feed')}
                  aria-current={activeTab === 'feed' ? 'page' : undefined}
                >
                  <i className="fas fa-newspaper"></i>
                  <span className="nav-label-full">Fil d'actualité</span>
                  <span className="nav-label-short">Fil</span>
                </button>
                <button 
                  type="button"
                  className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
                  onClick={() => setActiveTab('messages')}
                  aria-current={activeTab === 'messages' ? 'page' : undefined}
                >
                  <i className="fas fa-envelope"></i>
                  <span className="nav-label-full">Messages</span>
                  <span className="nav-label-short">Messages</span>
                </button>
                <button 
                  type="button"
                  className={`nav-item ${activeTab === 'friends' ? 'active' : ''}`}
                  onClick={() => setActiveTab('friends')}
                  aria-current={activeTab === 'friends' ? 'page' : undefined}
                >
                  <i className="fas fa-users"></i>
                  <span className="nav-label-full">Amis</span>
                  <span className="nav-label-short">Amis</span>
                </button>
                <button 
                  type="button"
                  className={`nav-item profile-nav-item social-profile-access ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedUserId(currentUserId || null)
                    setActiveTab('profile')
                  }}
                  aria-label="Profil social"
                  aria-current={activeTab === 'profile' ? 'page' : undefined}
                >
                  {currentUserProfile?.avatar_url ? (
                    <img src={currentUserProfile.avatar_url} alt="" className="profile-nav-avatar" />
                  ) : (
                    <div className="profile-nav-avatar-placeholder">
                      {(currentUserProfile?.display_name?.charAt(0) || 
                        currentUserProfile?.username?.charAt(0) || 
                        currentUserProfile?.email?.charAt(0) || 
                        'U').toUpperCase()}
                    </div>
                  )}
                  <span className="social-profile-label">{currentUserProfile?.display_name || currentUserProfile?.username || currentUserProfile?.email || 'Profil'}</span>
                  <span className="nav-label-short">Profil</span>
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="social-content">
        {activeTab === 'feed' && (
          <div className="feed-main tab-content active">
            <div className="feed-center">
              <CreatePost onPostCreated={refetch} />
              
              {error?.response?.status === 405 ? (
                <div className="empty-state">
                  <h3>⚠️ Backend non disponible</h3>
                  <p>Les endpoints du réseau social ne sont pas encore implémentés côté backend.</p>
                  <p style={{ fontSize: '0.9em', marginTop: '10px', color: 'var(--text-secondary)' }}>
                    Les routes suivantes doivent être créées :<br />
                    - GET /api/social/posts/feed<br />
                    - GET /api/social/friends<br />
                    - GET /api/social/friends/requests
                  </p>
                </div>
              ) : isLoading ? (
                <div className="loading">
                  <div className="loader"></div>
                </div>
              ) : posts.length === 0 ? (
                <div className="empty-state">
                  <h3>Aucun post pour le moment</h3>
                  <p>Soyez le premier à publier quelque chose !</p>
                </div>
              ) : (
                <div className="posts-list">
                  {posts.map(post => (
                    <PostCard 
                      key={post.id} 
                      post={post} 
                      onUpdate={refetch}
                      onUserClick={(userId) => {
                        setSelectedUserId(userId)
                        setActiveTab('profile')
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <FriendsSidebar onUserClick={(userId) => {
              setSelectedUserId(userId)
              setActiveTab('profile')
            }} />
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="tab-content active">
            <MessagesPanel 
              selectedConversation={selectedConversation}
              onSelectConversation={setSelectedConversation}
              onUserClick={(userId) => {
                setSelectedUserId(userId)
                setActiveTab('profile')
              }}
            />
          </div>
        )}

        {activeTab === 'friends' && (
          <div className="tab-content active">
            <FriendsSidebar 
              fullWidth 
              onUserClick={(userId) => {
                setSelectedUserId(userId)
                setActiveTab('profile')
              }}
            />
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-content active">
            <UserProfile 
              userId={resolveProfileUserId(
                selectedUserId,
                user,
                typeof localStorage !== 'undefined' ? localStorage.getItem('social_selectedUserId') : null,
                typeof localStorage !== 'undefined' ? localStorage.getItem('social_lastViewedUserId') : null,
              )}
              onBack={() => setActiveTab('feed')}
              onUserClick={(userId) => {
                setSelectedUserId(userId)
                setActiveTab('profile')
              }}
            />
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default SocialFeed

